import classNames from "classnames"
import React, { PropsWithChildren, forwardRef } from "react"

import { Color } from "../../../constants/V2/color"
import PillButton from "../../elements/V2/Buttons/PillButton"
import TextButton from "../../elements/V2/Buttons/TextButton"
import ArrowIcon from "../../elements/V2/Icons/ArrowIcon"
import Section from "../../elements/V2/Section"

import {
  getAccentColor,
  getBackgroundColorClass,
  getGradientDirectionClass,
  getGradientFromColorClass,
  getGradientToColorClass,
  getGradientViaColorClass,
} from "@utils/V2/color"

export type CTAProps = {
  ctaColor?: Color
  ctaText?: string
  ctaLink?: string | YocoCom.YocoLink
  ctaType?: "pill" | "text" | "textUnderlined"
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
}

type Props = {
  id?: string
  primaryBackgroundColor: Color
  secondaryBackgroundColor?: Color
  tertiaryBackgroundColor?: Color
  gradientDirection?: "to-b" | "to-br"
  secondaryCtaText?: string
  secondaryCtaLink?: string | YocoCom.YocoLink
  rounded?: boolean
  className?: string
} & CTAProps &
  React.DOMAttributes<HTMLDivElement>

const LayoutSection = forwardRef<HTMLDivElement, PropsWithChildren<Props>>(
  (
    {
      id,
      ctaType,
      ctaColor,
      ctaText,
      ctaLink,
      trackingEvent,
      trackingEventKey,
      trackingEventValue,
      primaryBackgroundColor,
      secondaryBackgroundColor,
      tertiaryBackgroundColor,
      gradientDirection = "to-br",
      secondaryCtaText,
      secondaryCtaLink,
      rounded = true,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const getCTA = (ctaType: string | undefined) => {
      if (ctaType === "pill") {
        return (
          <PillButton
            size="large"
            style="solid"
            text={ctaText ?? ""}
            linkUrl={ctaLink}
            trackingEvent={trackingEvent}
            trackingEventKey={trackingEventKey}
            trackingEventValue={trackingEventValue}
            className="self-center sm:self-auto"
            color={ctaColor ?? getAccentColor(primaryBackgroundColor)}
          />
        )
      } else if (ctaType === "text") {
        return (
          <TextButton
            style="icon"
            text={ctaText ?? ""}
            linkUrl={ctaLink}
            icon={<ArrowIcon />}
            trackingEvent={trackingEvent}
            trackingEventKey={trackingEventKey}
            trackingEventValue={trackingEventValue}
            className="self-center sm:self-auto"
            color={ctaColor ?? getAccentColor(primaryBackgroundColor)}
          />
        )
      } else if (ctaType === "textUnderlined") {
        return (
          <TextButton
            style="underlined-reverse"
            text={ctaText ?? ""}
            linkUrl={ctaLink}
            trackingEvent={trackingEvent}
            trackingEventKey={trackingEventKey}
            trackingEventValue={trackingEventValue}
            className="self-center sm:self-auto"
            color={ctaColor ?? getAccentColor(primaryBackgroundColor)}
          />
        )
      }
    }

    return (
      <Section
        id={id}
        ref={ref}
        className={classNames(
          "-mb-lg py-3xl relative",
          { "rounded-t-lg": rounded },
          secondaryBackgroundColor
            ? [
                getGradientDirectionClass(gradientDirection),
                getGradientFromColorClass(primaryBackgroundColor),
                tertiaryBackgroundColor
                  ? getGradientViaColorClass(secondaryBackgroundColor)
                  : undefined,
                getGradientToColorClass(
                  tertiaryBackgroundColor || secondaryBackgroundColor
                ),
                tertiaryBackgroundColor && "sm:from-15% sm:via-40% sm:to-75%",
              ]
            : getBackgroundColorClass(primaryBackgroundColor),
          { "space-y-60": ctaText && ctaLink },
          className
        )}
        {...props}
      >
        {children}

        {ctaLink || secondaryCtaLink ? (
          <div className="flex flex-col justify-center gap-32 sm:flex-row">
            {ctaText && ctaLink ? getCTA(ctaType) : null}
            {secondaryCtaLink && secondaryCtaText ? (
              <TextButton
                style="text"
                text={secondaryCtaText}
                textSize="body-sm"
                linkUrl={secondaryCtaLink}
                className="self-center pt-0 sm:self-auto sm:pt-20"
                color={getAccentColor(primaryBackgroundColor)}
              />
            ) : null}
          </div>
        ) : null}
      </Section>
    )
  }
)

LayoutSection.displayName = "LayoutSection"

export default LayoutSection
